import { useTippy } from "vue-tippy";
import type { Directive, DirectiveBinding } from "vue";
import debounce from "lodash/debounce";

interface TippyInstance {
  destroy: () => void;
  setContent: (content: string) => void;
}

interface TooltipableElement extends HTMLElement {
  _tooltipInstance?: TippyInstance;
  _mutationObserver?: MutationObserver;
  _previousText?: string;
}

const directive: Directive = {
  mounted(el: TooltipableElement, binding: DirectiveBinding) {
    setupTooltip(el, binding);
  },

  unmounted(el: TooltipableElement) {
    cleanupTooltip(el);
  },
};

function setupTooltip(el: TooltipableElement, binding: DirectiveBinding): void {
  const theme = binding.modifiers?.themeLight ? "light" : undefined;

  // If there's a <p> inside, use that instead
  const target = (el.querySelector("p") as TooltipableElement) || el;

  const getCurrentText = () => target.textContent || "";

  const isTextTruncated = () => {
    if (target.className.includes("line-clamp-")) {
      return target.scrollHeight > target.clientHeight;
    }
    return target.scrollWidth > target.clientWidth;
  };

  const tippyInstance = useTippy(target, {
    content: getCurrentText(),
    theme,
    onShow: () => {
      const currentText = getCurrentText();

      if (!currentText || !isTextTruncated()) {
        return false;
      }

      tippyInstance?.setContent(currentText);
    },
    allowHTML: false,
    maxWidth: 400,
  });

  target._tooltipInstance = tippyInstance;
  target._previousText = getCurrentText();

  const observer = new MutationObserver(
    debounce((mutations: MutationRecord[]) => {
      if (mutations.some((m) => m.type === "childList" || m.type === "characterData")) {
        tippyInstance?.setContent(getCurrentText());
      }
    }, 300)
  );

  observer.observe(target, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  target._mutationObserver = observer;
}

function cleanupTooltip(el: TooltipableElement): void {
  if (el._tooltipInstance?.destroy) {
    el._tooltipInstance.destroy();
    el._tooltipInstance = undefined;
  }

  if (el._mutationObserver?.disconnect) {
    el._mutationObserver.disconnect();
    el._mutationObserver = undefined;
  }

  el._previousText = undefined;
}

export default directive;
